import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { EmpresaProvider } from "./contexts/EmpresaContext";
import { lazy, Suspense } from "react";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import SystemHealthBanner from "./components/SystemHealthBanner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import "./App.css";

// Eager: Login and Home (critical path)
import Login from "./pages/Login";
import Home from "./pages/Home";
import ExportarDB from "./pages/ExportarDB";
import ResetSistema from "./pages/ResetSistema";

// Lazy: All other pages (loaded on demand)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Galpones = lazy(() => import("./pages/Galpones"));
const TiempoReal = lazy(() => import("./pages/TiempoReal"));
const ImportacionesEco = lazy(() => import("./pages/ImportacionesEco"));
const ReporteDiarioEco = lazy(() => import("./pages/ReporteDiarioEco"));
const FinanzasEco = lazy(() => import("./pages/FinanzasEco"));
const Ventas = lazy(() => import("./pages/Ventas"));
const Clientes = lazy(() => import("./pages/Clientes"));
const RRHH = lazy(() => import("./pages/RRHH"));
const RRHHCompleto = lazy(() => import("./pages/RRHHCompleto"));
const Compras = lazy(() => import("./pages/Compras"));
const LogisticaGPS = lazy(() => import("./pages/LogisticaGPS"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const SolicitudesCambio = lazy(() => import("./pages/SolicitudesCambio"));
const Auditoria = lazy(() => import("./pages/Auditoria"));
const WhatsApp = lazy(() => import("./pages/WhatsApp"));
const BotWhatsApp = lazy(() => import("./pages/BotWhatsApp"));
const Asistencia = lazy(() => import("./pages/Asistencia"));
const VentasHistorico = lazy(() => import("./pages/VentasHistorico"));
const BancoEco = lazy(() => import("./pages/BancoEco"));
const DeudaEdmundo = lazy(() => import("./pages/DeudaEdmundo"));
const InventarioEco = lazy(() => import("./pages/InventarioEco"));

// Page loading fallback
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-3 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-slate-500 text-sm">Cargando...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const SuperadminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "superadmin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Wrapper for lazy-loaded pages inside Layout with error boundary
const LazyPage = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/exportar-db" element={<SuperadminRoute><ExportarDB /></SuperadminRoute>} />
      <Route path="/reset-sistema" element={<SuperadminRoute><ResetSistema /></SuperadminRoute>} />
      <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      
      {/* ECO Bolivia routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout><LazyPage><Dashboard /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/inventario" element={<Navigate to="/galpones" replace />} />
      <Route path="/galpones" element={<ProtectedRoute><Layout><LazyPage><Galpones /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/tiempo-real" element={<ProtectedRoute><Layout><LazyPage><TiempoReal /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/importaciones" element={<Navigate to="/importaciones-eco" replace />} />
      <Route path="/importaciones-eco" element={<ProtectedRoute><Layout><LazyPage><ImportacionesEco /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/reporte-diario-eco" element={<ProtectedRoute><Layout><LazyPage><ReporteDiarioEco /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/finanzas-eco" element={<ProtectedRoute><Layout><LazyPage><FinanzasEco /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/inventario-eco" element={<Navigate to="/galpones" replace />} />
      <Route path="/ventas" element={<ProtectedRoute><Layout><LazyPage><Ventas /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/clientes" element={<ProtectedRoute><Layout><LazyPage><Clientes /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/rrhh" element={<ProtectedRoute><Layout><LazyPage><RRHH /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/rrhh-completo" element={<ProtectedRoute><Layout><LazyPage><RRHHCompleto /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/compras" element={<ProtectedRoute><Layout><LazyPage><Compras /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/logistica" element={<ProtectedRoute><Layout><LazyPage><LogisticaGPS /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/usuarios" element={<ProtectedRoute><Layout><LazyPage><Usuarios /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/solicitudes" element={<ProtectedRoute><Layout><LazyPage><SolicitudesCambio /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/auditoria" element={<ProtectedRoute><Layout><LazyPage><Auditoria /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute><Layout><LazyPage><WhatsApp /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/bot-whatsapp" element={<ProtectedRoute><Layout><LazyPage><BotWhatsApp /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/asistencia" element={<ProtectedRoute><Layout><LazyPage><Asistencia /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/ventas-historico" element={<ProtectedRoute><Layout><LazyPage><VentasHistorico /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/banco-eco" element={<ProtectedRoute><Layout><LazyPage><BancoEco /></LazyPage></Layout></ProtectedRoute>} />
      <Route path="/deuda-edmundo" element={<ProtectedRoute><Layout><LazyPage><DeudaEdmundo /></LazyPage></Layout></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <EmpresaProvider>
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                fontSize: '16px',
                padding: '16px',
              },
            }}
          />
          <PWAInstallPrompt />
          <SystemHealthBanner />
          <AppRoutes />
        </EmpresaProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
